import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Database, Calendar, Clock, ChevronRight, Loader2, Search, Image, Trash2, X, CheckSquare, Square, AlertTriangle, Grid, List, Info, BarChart3, CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import BackButton from "@/components/BackButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "business", label: "Business", color: "#3b82f6" },
  { value: "wealth", label: "Wealth", color: "#f59e0b" },
  { value: "career", label: "Career", color: "#8b5cf6" },
  { value: "health", label: "Health", color: "#10b981" },
  { value: "academics", label: "Academics", color: "#06b6d4" },
  { value: "relationship", label: "Relationship", color: "#ec4899" },
  { value: "recognition", label: "Recognition", color: "#f97316" },
];

export default function ScrapedDataPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chartImages, setChartImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [viewMode, setViewMode] = useState("compact"); // "compact" or "thumbnails"
  const [activeSection, setActiveSection] = useState("charts");

  // Info images state
  const [infoDate, setInfoDate] = useState(new Date());
  const [infoCalendarOpen, setInfoCalendarOpen] = useState(false);
  const [infoImages, setInfoImages] = useState([]);
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoSelectMode, setInfoSelectMode] = useState(false);
  const [selectedInfoKeys, setSelectedInfoKeys] = useState(new Set());
  const [infoBulkDeleting, setInfoBulkDeleting] = useState(false);

  // Category images state
  const [catDate, setCatDate] = useState(new Date());
  const [catCalendarOpen, setCatCalendarOpen] = useState(false);
  const [catHour, setCatHour] = useState("1");
  const [catImages, setCatImages] = useState({});
  const [catLoading, setCatLoading] = useState(false);
  const [catSelectMode, setCatSelectMode] = useState(false);
  const [selectedCatKeys, setSelectedCatKeys] = useState(new Set());
  const [catBulkDeleting, setCatBulkDeleting] = useState(false);

  useEffect(() => {
    fetchScrapedData();
  }, []);

  const fetchScrapedData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/qimen/lookup/status`);
      const json = await res.json();
      setData(json);
      setChartImages(json.chart_images || {});
    } catch (err) {
      console.error("Failed to fetch scraped data:", err);
      setError("Failed to load scraped data");
    } finally {
      setLoading(false);
    }
  };

  // Parse and categorize chart keys
  const parseCharts = (charts) => {
    if (!charts) return { dateCharts: [], jiaziCharts: [] };

    const dateCharts = [];
    const jiaziCharts = [];

    charts.forEach((key) => {
      // Date format: YYYY_MM_DD_HH
      const dateParts = key.split("_");
      if (dateParts.length === 4 && /^\d{4}$/.test(dateParts[0])) {
        const [year, month, day, hour] = dateParts;
        const hourNum = parseInt(hour);
        // Convert stored hour (0-23) to display hour (01:00-24:00)
        // Hour 0 displays as 24:00, hours 1-23 display as 01:00-23:00
        const displayHour = hourNum === 0 ? 24 : hourNum;
        dateCharts.push({
          key,
          year,
          month,
          day,
          hour,
          date: `${year}-${month}-${day}`,
          time: `${String(displayHour).padStart(2, '0')}:00`,
          sortKey: `${year}${month}${day}${String(hourNum === 0 ? 24 : hourNum).padStart(2, '0')}`,
        });
      } else {
        // Jiazi format: 甲子_yang_9
        jiaziCharts.push({
          key,
          jiazi: dateParts[0] || key,
          dun: dateParts[1] || "",
          ju: dateParts[2] || "",
        });
      }
    });

    // Sort date charts by date/time (01:00 comes first, 24:00 comes last)
    dateCharts.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    return { dateCharts, jiaziCharts };
  };

  // Group date charts by date
  const groupByDate = (dateCharts) => {
    const groups = {};
    dateCharts.forEach((chart) => {
      if (!groups[chart.date]) {
        groups[chart.date] = [];
      }
      groups[chart.date].push(chart);
    });
    return groups;
  };

  // Filter charts based on search term
  const filterCharts = (charts, term) => {
    if (!term) return charts;
    const lower = term.toLowerCase();
    // Support both dash and underscore formats
    const normalizedTerm = lower.replace(/-/g, '_');
    return charts.filter((c) => {
      const normalizedKey = c.key.toLowerCase();
      return normalizedKey.includes(lower) || normalizedKey.includes(normalizedTerm);
    });
  };

  const handleViewChart = (chart) => {
    if (selectMode) {
      toggleSelection(chart.key);
      return;
    }
    
    if (chart.year) {
      // Date-based chart - URL only contains date/hour params, no image URL
      const params = new URLSearchParams({
        year: chart.year,
        month: String(parseInt(chart.month)),
        day: String(parseInt(chart.day)),
        hour: String(parseInt(chart.hour)),
        minute: "0",
        system: "selectZhiRun",
      });
      
      // Open in new window/tab - image will be fetched automatically
      window.open(`/result?${params.toString()}`, '_blank');
    }
  };

  const toggleSelection = (key) => {
    setSelectedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleSelectAll = (charts) => {
    const allKeys = charts.map(c => c.key);
    const allSelected = allKeys.every(k => selectedKeys.has(k));
    
    if (allSelected) {
      // Deselect all
      setSelectedKeys(prev => {
        const newSet = new Set(prev);
        allKeys.forEach(k => newSet.delete(k));
        return newSet;
      });
    } else {
      // Select all
      setSelectedKeys(prev => {
        const newSet = new Set(prev);
        allKeys.forEach(k => newSet.add(k));
        return newSet;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedKeys.size === 0) return;
    
    // Show confirmation step first
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/qimen/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date_keys: Array.from(selectedKeys) }),
      });
      
      const result = await res.json();
      
      if (result.success) {
        setSelectedKeys(new Set());
        setSelectMode(false);
        setShowConfirm(false);
        fetchScrapedData(); // Refresh data
      } else {
        alert(`Delete failed: ${result.error}`);
      }
    } catch (err) {
      console.error("Bulk delete failed:", err);
      alert("Failed to delete charts. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const cancelConfirm = () => {
    setShowConfirm(false);
  };

  const cancelSelectMode = () => {
    setSelectMode(false);
    setSelectedKeys(new Set());
    setShowConfirm(false);
  };

  // ── Info Images ──
  useEffect(() => {
    if (activeSection === "info") fetchInfoImages();
  }, [infoDate, activeSection]);

  const fetchInfoImages = async () => {
    setInfoLoading(true);
    try {
      const y = infoDate.getFullYear(), m = infoDate.getMonth() + 1, d = infoDate.getDate();
      const res = await fetch(`${API}/info/list?year=${y}&month=${m}&day=${d}`);
      const data = await res.json();
      setInfoImages(data.success ? (data.images || []) : []);
    } catch { setInfoImages([]); }
    finally { setInfoLoading(false); }
  };

  const deleteInfoImage = async (img) => {
    const label = `${format(infoDate, "MMM d")} ${String(img.display_hour).padStart(2, "0")}:00`;
    if (!window.confirm(`Delete info image for ${label}?`)) return;
    try {
      await fetch(`${API}/info/delete?year=${img.year}&month=${img.month}&day=${img.day}&hour=${img.display_hour}`, { method: "DELETE" });
      fetchInfoImages();
    } catch (err) { console.error(err); }
  };

  const toggleInfoSelect = (key) => {
    setSelectedInfoKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAllInfo = () => {
    if (selectedInfoKeys.size === infoImages.length) {
      setSelectedInfoKeys(new Set());
    } else {
      setSelectedInfoKeys(new Set(infoImages.map(img => img.date_key)));
    }
  };

  const bulkDeleteInfo = async () => {
    if (selectedInfoKeys.size === 0) return;
    if (!window.confirm(`Delete ${selectedInfoKeys.size} info image(s)?`)) return;
    setInfoBulkDeleting(true);
    for (const key of selectedInfoKeys) {
      const img = infoImages.find(i => i.date_key === key);
      if (img) {
        try {
          await fetch(`${API}/info/delete?year=${img.year}&month=${img.month}&day=${img.day}&hour=${img.display_hour}`, { method: "DELETE" });
        } catch (err) { console.error(err); }
      }
    }
    setSelectedInfoKeys(new Set());
    setInfoSelectMode(false);
    setInfoBulkDeleting(false);
    fetchInfoImages();
  };

  // ── Category Images ──
  useEffect(() => {
    if (activeSection === "category") fetchCatImages();
  }, [catDate, catHour, activeSection]);

  const fetchCatImages = async () => {
    setCatLoading(true);
    try {
      const y = catDate.getFullYear(), m = catDate.getMonth() + 1, d = catDate.getDate();
      const res = await fetch(`${API}/category/available?year=${y}&month=${m}&day=${d}&hour=${catHour}`);
      const data = await res.json();
      setCatImages(data.success ? (data.available || {}) : {});
    } catch { setCatImages({}); }
    finally { setCatLoading(false); }
  };

  const deleteCatImage = async (cat) => {
    const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
    if (!window.confirm(`Delete ${catLabel} image?`)) return;
    try {
      const y = catDate.getFullYear(), m = catDate.getMonth() + 1, d = catDate.getDate();
      await fetch(`${API}/category/delete?year=${y}&month=${m}&day=${d}&hour=${catHour}&category=${cat}`, { method: "DELETE" });
      fetchCatImages();
    } catch (err) { console.error(err); }
  };

  const toggleCatSelect = (key) => {
    setSelectedCatKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAllCat = () => {
    const available = CATEGORIES.filter(c => catImages[c.value]).map(c => c.value);
    if (selectedCatKeys.size === available.length) {
      setSelectedCatKeys(new Set());
    } else {
      setSelectedCatKeys(new Set(available));
    }
  };

  const bulkDeleteCat = async () => {
    if (selectedCatKeys.size === 0) return;
    if (!window.confirm(`Delete ${selectedCatKeys.size} category image(s)?`)) return;
    setCatBulkDeleting(true);
    const y = catDate.getFullYear(), m = catDate.getMonth() + 1, d = catDate.getDate();
    for (const cat of selectedCatKeys) {
      try {
        await fetch(`${API}/category/delete?year=${y}&month=${m}&day=${d}&hour=${catHour}&category=${cat}`, { method: "DELETE" });
      } catch (err) { console.error(err); }
    }
    setSelectedCatKeys(new Set());
    setCatSelectMode(false);
    setCatBulkDeleting(false);
    fetchCatImages();
  };

  const HOURS = Array.from({ length: 24 }, (_, i) => ({
    value: String(i + 1),
    label: `${String(i + 1).padStart(2, "0")}:00`,
  }));

  if (loading) {
    return (
      <div className="scraped-data-page" data-testid="scraped-data-loading">
        <div className="loading-container">
          <Loader2 className="animate-spin" size={48} />
          <p>Loading scraped data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scraped-data-page" data-testid="scraped-data-error">
        <div className="error-container">
          <p>{error}</p>
          <Button onClick={() => navigate("/")} data-testid="back-home-btn">
            <ArrowLeft size={16} /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const { dateCharts } = parseCharts(data?.charts || []);
  const filteredDateCharts = filterCharts(dateCharts, searchTerm);
  const groupedByDate = groupByDate(filteredDateCharts);
  const dateKeys = Object.keys(groupedByDate).sort();

  return (
    <div className="scraped-data-page" data-testid="scraped-data-page">
      {/* Top Bar */}
      <div className="result-topbar">
        <BackButton />
        <div className="topbar-title" data-testid="topbar-title">
          <Database size={20} />
          Scraped Data
        </div>
        <div className="topbar-actions">
          {activeSection === 'charts' && (
            <>
              <Button
                variant="ghost"
                className="view-toggle-btn"
                onClick={() => setViewMode(viewMode === "compact" ? "thumbnails" : "compact")}
                data-testid="view-toggle-btn"
              >
                {viewMode === "compact" ? <Grid size={16} /> : <List size={16} />}
                {viewMode === "compact" ? "Thumbnails" : "Compact"}
              </Button>
              
              {!selectMode ? (
                <Button
                  variant="ghost"
                  className="select-mode-btn"
                  onClick={() => setSelectMode(true)}
                  data-testid="enter-select-mode-btn"
                >
                  <Trash2 size={16} />
                  Delete
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  className="cancel-select-btn"
                  onClick={cancelSelectMode}
                  data-testid="cancel-select-btn"
                >
                  <X size={16} />
                  Cancel
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="manage-section-tabs" data-testid="manage-tabs">
        <button
          className={`manage-tab ${activeSection === 'charts' ? 'active' : ''}`}
          data-state={activeSection === 'charts' ? 'active' : 'inactive'}
          onClick={() => { setActiveSection('charts'); setInfoSelectMode(false); setCatSelectMode(false); setSelectedInfoKeys(new Set()); setSelectedCatKeys(new Set()); }}
          data-testid="manage-tab-charts"
        >
          <Database size={16} /> Charts
        </button>
        <button
          className={`manage-tab ${activeSection === 'info' ? 'active' : ''}`}
          data-state={activeSection === 'info' ? 'active' : 'inactive'}
          onClick={() => { setActiveSection('info'); cancelSelectMode(); setCatSelectMode(false); setSelectedCatKeys(new Set()); }}
          data-testid="manage-tab-info"
        >
          <Info size={16} /> Important Info
        </button>
        <button
          className={`manage-tab ${activeSection === 'category' ? 'active' : ''}`}
          data-state={activeSection === 'category' ? 'active' : 'inactive'}
          onClick={() => { setActiveSection('category'); cancelSelectMode(); setInfoSelectMode(false); setSelectedInfoKeys(new Set()); }}
          data-testid="manage-tab-category"
        >
          <BarChart3 size={16} /> Category Forecasts
        </button>
      </div>

      {/* ── CHARTS SECTION ── */}
      {activeSection === 'charts' && (<>
      {/* Stats Summary */}
      <div className="scraped-stats" data-testid="scraped-stats">
        <div className="stat-card">
          <div className="stat-number">{data?.total_charts || 0}</div>
          <div className="stat-label">Total Charts</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{dateCharts.length}</div>
          <div className="stat-label">Date-Based</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{dateKeys.length}</div>
          <div className="stat-label">Days Covered</div>
        </div>
      </div>

      {/* Search */}
      <div className="scraped-search">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <Input
            placeholder="Search by date or key..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Selection Bar - Shows when in select mode */}
      {selectMode && !showConfirm && (
        <div className="selection-bar" data-testid="selection-bar">
          <div className="selection-info">
            <CheckSquare size={16} />
            <span>{selectedKeys.size} selected</span>
          </div>
          <div className="selection-actions">
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleSelectAll(filteredDateCharts)}
              data-testid="select-all-btn"
            >
              {filteredDateCharts.every(c => selectedKeys.has(c.key)) ? "Deselect All" : "Select All"}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={selectedKeys.size === 0}
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={14} />
              Delete {selectedKeys.size > 0 ? `(${selectedKeys.size})` : ""}
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation Bar - Shows when confirming delete */}
      {showConfirm && (
        <div className="confirm-delete-bar" data-testid="confirm-delete-bar">
          <div className="confirm-warning">
            <AlertTriangle size={20} />
            <div className="confirm-text">
              <strong>Delete {selectedKeys.size} chart(s)?</strong>
              <span>This action cannot be undone.</span>
            </div>
          </div>
          <div className="confirm-actions">
            <Button
              variant="outline"
              size="sm"
              onClick={cancelConfirm}
              disabled={deleting}
              data-testid="cancel-confirm-btn"
            >
              Go Back
            </Button>
            <Button
              className="confirm-delete-final-btn"
              size="sm"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={14} />
                  YES, DELETE PERMANENTLY
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="scraped-content">
        {/* Date-Based Charts Section */}
        {filteredDateCharts.length > 0 && (
          <div className="scraped-section">
            <h2 className="section-title">
              <Calendar size={18} />
              Date-Based Charts ({filteredDateCharts.length})
            </h2>
            <div className="date-groups">
              {dateKeys.map((date) => (
                <div key={date} className="date-group" data-testid={`date-group-${date}`}>
                  <div className="date-header">
                    {selectMode && (
                      <button
                        className="date-select-all"
                        onClick={() => toggleSelectAll(groupedByDate[date])}
                        data-testid={`select-all-${date}`}
                      >
                        {groupedByDate[date].every(c => selectedKeys.has(c.key)) ? (
                          <CheckSquare size={14} />
                        ) : (
                          <Square size={14} />
                        )}
                      </button>
                    )}
                    <Calendar size={14} />
                    {date}
                    <span className="chart-count">({groupedByDate[date].length} charts)</span>
                  </div>
                  {viewMode === "compact" ? (
                    <div className="time-slots">
                      {groupedByDate[date].map((chart) => {
                        const hasImage = !!chartImages[chart.key];
                        const isSelected = selectedKeys.has(chart.key);
                        return (
                          <button
                            key={chart.key}
                            className={`time-slot ${hasImage ? "has-image" : ""} ${isSelected ? "selected" : ""} ${selectMode ? "select-mode" : ""}`}
                            onClick={() => handleViewChart(chart)}
                            data-testid={`time-slot-${chart.key}`}
                          >
                            {selectMode && (
                              <span className="slot-checkbox">
                                {isSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                              </span>
                            )}
                            {hasImage && <Image size={12} className="image-indicator" />}
                            <Clock size={12} />
                            {chart.time}
                            {!selectMode && <ChevronRight size={14} />}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="thumbnail-grid">
                      {groupedByDate[date].map((chart) => {
                        const imageUrl = chartImages[chart.key];
                        const isSelected = selectedKeys.has(chart.key);
                        return (
                          <div
                            key={chart.key}
                            className={`thumbnail-card ${isSelected ? "selected" : ""} ${selectMode ? "select-mode" : ""}`}
                            onClick={() => handleViewChart(chart)}
                            data-testid={`thumbnail-${chart.key}`}
                          >
                            {selectMode && (
                              <span className="thumbnail-checkbox">
                                {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                              </span>
                            )}
                            <div className="thumbnail-image-wrap">
                              {imageUrl ? (
                                <img 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${imageUrl}`} 
                                  alt={`Chart ${chart.time}`}
                                  loading="lazy"
                                />
                              ) : (
                                <div className="thumbnail-placeholder">
                                  <Image size={24} />
                                  <span>No image</span>
                                </div>
                              )}
                            </div>
                            <div className="thumbnail-label">
                              <Clock size={12} />
                              {chart.time}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {filteredDateCharts.length === 0 && (
          <div className="no-results">
            <p>No charts found matching "{searchTerm}"</p>
          </div>
        )}
      </div>
      </>)}

      {/* ── IMPORTANT INFO SECTION ── */}
      {activeSection === 'info' && (
        <div className="manage-info-section" data-testid="manage-info-section">
          <div className="manage-filter-bar">
            <Popover open={infoCalendarOpen} onOpenChange={setInfoCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="manage-date-btn" data-testid="manage-info-date">
                  <CalendarIcon size={16} />
                  {format(infoDate, "MMMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="calendar-popover" align="center">
                <CalendarComponent mode="single" selected={infoDate} onSelect={(d) => { if (d) { setInfoDate(d); setInfoCalendarOpen(false); } }} initialFocus />
              </PopoverContent>
            </Popover>
            {infoImages.length > 0 && (
              <Button
                variant={infoSelectMode ? "default" : "outline"}
                size="sm"
                onClick={() => { setInfoSelectMode(!infoSelectMode); setSelectedInfoKeys(new Set()); }}
                data-testid="info-select-mode-btn"
              >
                {infoSelectMode ? <><X size={14} /> Cancel</> : <><CheckSquare size={14} /> Select</>}
              </Button>
            )}
          </div>

          {infoSelectMode && infoImages.length > 0 && (
            <div className="manage-bulk-bar" data-testid="info-bulk-bar">
              <Button variant="ghost" size="sm" onClick={selectAllInfo} data-testid="info-select-all">
                {selectedInfoKeys.size === infoImages.length ? <><CheckSquare size={14} /> Deselect All</> : <><Square size={14} /> Select All</>}
              </Button>
              <span className="bulk-count">{selectedInfoKeys.size} selected</span>
              <Button
                variant="destructive"
                size="sm"
                disabled={selectedInfoKeys.size === 0 || infoBulkDeleting}
                onClick={bulkDeleteInfo}
                data-testid="info-bulk-delete-btn"
              >
                {infoBulkDeleting ? <><Loader2 size={14} className="animate-spin" /> Deleting...</> : <><Trash2 size={14} /> Delete Selected</>}
              </Button>
            </div>
          )}

          {infoLoading ? (
            <div className="manage-loading"><Loader2 className="animate-spin" size={32} /><p>Loading...</p></div>
          ) : infoImages.length === 0 ? (
            <div className="manage-empty"><p>No info images for {format(infoDate, "MMMM d, yyyy")}</p></div>
          ) : (
            <div className="manage-images-grid">
              {infoImages.sort((a, b) => a.display_hour - b.display_hour).map(img => (
                <div
                  key={img.date_key}
                  className={`manage-image-card ${infoSelectMode && selectedInfoKeys.has(img.date_key) ? 'selected' : ''}`}
                  onClick={infoSelectMode ? () => toggleInfoSelect(img.date_key) : undefined}
                  data-testid={`manage-info-${img.date_key}`}
                >
                  {infoSelectMode && (
                    <div className="manage-select-check">
                      {selectedInfoKeys.has(img.date_key) ? <CheckSquare size={18} /> : <Square size={18} />}
                    </div>
                  )}
                  <div className="manage-image-preview">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/info_images/${img.filename}`} alt={`Info ${img.display_hour}:00`} />
                  </div>
                  <div className="manage-image-footer">
                    <span className="manage-image-label">
                      <Clock size={14} />
                      {String(img.display_hour).padStart(2, "0")}:00
                    </span>
                    {!infoSelectMode && (
                      <Button variant="ghost" size="sm" className="manage-delete-btn" onClick={() => deleteInfoImage(img)} data-testid={`delete-info-${img.date_key}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CATEGORY FORECASTS SECTION ── */}
      {activeSection === 'category' && (
        <div className="manage-info-section" data-testid="manage-category-section">
          <div className="manage-filter-bar">
            <Popover open={catCalendarOpen} onOpenChange={setCatCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="manage-date-btn" data-testid="manage-cat-date">
                  <CalendarIcon size={16} />
                  {format(catDate, "MMMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="calendar-popover" align="center">
                <CalendarComponent mode="single" selected={catDate} onSelect={(d) => { if (d) { setCatDate(d); setCatCalendarOpen(false); } }} initialFocus />
              </PopoverContent>
            </Popover>
            <Select value={catHour} onValueChange={setCatHour}>
              <SelectTrigger className="manage-hour-select" data-testid="manage-cat-hour">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map(h => (
                  <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {Object.keys(catImages).length > 0 && (
              <Button
                variant={catSelectMode ? "default" : "outline"}
                size="sm"
                onClick={() => { setCatSelectMode(!catSelectMode); setSelectedCatKeys(new Set()); }}
                data-testid="cat-select-mode-btn"
              >
                {catSelectMode ? <><X size={14} /> Cancel</> : <><CheckSquare size={14} /> Select</>}
              </Button>
            )}
          </div>

          {catSelectMode && Object.keys(catImages).length > 0 && (
            <div className="manage-bulk-bar" data-testid="cat-bulk-bar">
              <Button variant="ghost" size="sm" onClick={selectAllCat} data-testid="cat-select-all">
                {selectedCatKeys.size === CATEGORIES.filter(c => catImages[c.value]).length ? <><CheckSquare size={14} /> Deselect All</> : <><Square size={14} /> Select All</>}
              </Button>
              <span className="bulk-count">{selectedCatKeys.size} selected</span>
              <Button
                variant="destructive"
                size="sm"
                disabled={selectedCatKeys.size === 0 || catBulkDeleting}
                onClick={bulkDeleteCat}
                data-testid="cat-bulk-delete-btn"
              >
                {catBulkDeleting ? <><Loader2 size={14} className="animate-spin" /> Deleting...</> : <><Trash2 size={14} /> Delete Selected</>}
              </Button>
            </div>
          )}

          {catLoading ? (
            <div className="manage-loading"><Loader2 className="animate-spin" size={32} /><p>Loading...</p></div>
          ) : Object.keys(catImages).length === 0 ? (
            <div className="manage-empty"><p>No category images for {format(catDate, "MMM d, yyyy")} {catHour.padStart(2, "0")}:00</p></div>
          ) : (
            <div className="manage-images-grid">
              {CATEGORIES.filter(c => catImages[c.value]).map(c => (
                <div
                  key={c.value}
                  className={`manage-image-card ${catSelectMode && selectedCatKeys.has(c.value) ? 'selected' : ''}`}
                  onClick={catSelectMode ? () => toggleCatSelect(c.value) : undefined}
                  data-testid={`manage-cat-${c.value}`}
                >
                  {catSelectMode && (
                    <div className="manage-select-check">
                      {selectedCatKeys.has(c.value) ? <CheckSquare size={18} /> : <Square size={18} />}
                    </div>
                  )}
                  <div className="manage-image-preview">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${catImages[c.value]}`} alt={c.label} />
                  </div>
                  <div className="manage-image-footer">
                    <span className="manage-image-label" style={{ color: c.color }}>
                      {c.label}
                    </span>
                    {!catSelectMode && (
                      <Button variant="ghost" size="sm" className="manage-delete-btn" onClick={() => deleteCatImage(c.value)} data-testid={`delete-cat-${c.value}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

        {/* Footer */}
        <footer className="page-footer">
          <p className="footer-copyright">Copyright © 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.</p>
        </footer>
    </div>
  );
}
